import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, X, Wand2, Upload, Image as ImageIcon, Download, Lock, Sparkles,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { setSeoMetadata } from '../lib/seo';
import BackgroundRemover from '../tools/background-remover/App';
import '../tools/background-remover/styles.css';

/**
 * Free Tools -> Background Remover.
 *
 * Same pattern as the QR Code Generator: an info/marketing landing page
 * (what it does, why it's free, a forward-looking "more for registered
 * accounts" teaser) with a CTA that slides the actual tool in from the
 * right - same drawer pattern used across the Free Tools section.
 *
 * The tool itself is untouched: a self-contained module under
 * tools/background-remover, ported from the standalone Cutout build. All
 * inference happens in a Web Worker against ONNX Runtime served from our
 * own /bg-remover/ directory, so nothing about the image leaves the
 * visitor's machine and there is no per-use cost at any traffic level.
 * Its own CSS expects up to ~1040px of width, so the drawer here is wide
 * (max-w-4xl), unlike the QR tool's narrower form-style drawer.
 *
 * .bgr-root scopes the tool's stylesheet; without it the tool's generic
 * class names (.panel, .hero, .row) would collide with the rest of the site.
 */
const FEATURES = [
  { icon: Lock, title: 'Nothing uploaded', body: 'Every pixel stays on your device - there is no server to upload to.' },
  { icon: Sparkles, title: 'No account needed', body: 'Just drop an image in and get a transparent PNG back.' },
  { icon: Download, title: 'Unlimited and free', body: 'No watermark, no per-image limit, no catch.' },
];

const STEPS = [
  { icon: Upload, title: 'Drop in a photo', body: 'Any image with a clear subject works best.' },
  { icon: Wand2, title: 'AI removes the background', body: 'Runs right in your browser, usually in a couple of seconds.' },
  { icon: ImageIcon, title: 'Download the result', body: 'Get back a transparent PNG, ready to use anywhere.' },
];

const BackgroundRemoverPage = () => {
  const { siteName } = useSiteSettings();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setSeoMetadata({
      title: 'Free Background Remover',
      description:
        'Remove the background from any image, free. Runs entirely in your browser — '
        + 'your image is never uploaded. No account, no watermark.',
      keywords:
        'free background remover, remove image background, transparent png, '
        + 'cut out background, browser background remover',
      canonicalPath: '/tools/background-remover',
    });
  }, [siteName]);

  // Lock background scroll while the drawer is open.
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  return (
    <>
      <div className="min-h-screen bg-[#1a1c1b]">
        <Header />

        {/* Hero / info landing page */}
        <section className="pt-32 pb-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <Link
              to="/tools"
              className="inline-flex items-center gap-1.5 text-[#888680] hover:text-[#FF6B00] text-sm mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              All free tools
            </Link>
            <span className="inline-block px-3 py-1 rounded-full border border-[#FF6B00]/30 text-[#FF6B00] text-xs font-semibold tracking-wide mb-4">
              FREE TOOLS
            </span>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#FF6B00]/10 mb-6">
              <Wand2 className="w-8 h-8 text-[#FF6B00]" />
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">Background Remover</h1>
            <p className="text-[#888680] text-lg max-w-xl mx-auto mb-8">
              Drop in a photo, get a transparent PNG back &mdash; free, no account needed, nothing
              ever leaves your device.
            </p>
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#FF6B00] hover:bg-[#e05f00] text-white font-semibold rounded-full text-lg transition-colors"
              data-testid="open-bg-remover-button"
            >
              Remove a Background <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </section>

        {/* Feature highlights */}
        <section className="pb-16">
          <div className="max-w-4xl mx-auto px-4 grid sm:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-[#302f2c] border border-[#4a3520] rounded-2xl p-6 text-center">
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#FF6B00]/10 text-[#FF6B00] mb-4">
                  <f.icon className="w-5 h-5" />
                </div>
                <h3 className="text-white font-semibold mb-1.5">{f.title}</h3>
                <p className="text-[#888680] text-sm">{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className="pb-16">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-white font-bold text-2xl text-center mb-10">How it works</h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {STEPS.map((s, i) => (
                <div key={s.title} className="text-center">
                  <div className="relative inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#302f2c] border border-[#4a3520] text-[#FF6B00] mb-4">
                    <s.icon className="w-6 h-6" />
                    <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-[#FF6B00] text-white text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                  </div>
                  <h3 className="text-white font-semibold mb-1.5">{s.title}</h3>
                  <p className="text-[#888680] text-sm">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Sign-in teaser */}
        <section className="pb-24">
          <div className="max-w-3xl mx-auto px-4">
            <div className="bg-gradient-to-br from-[#FF6B00]/10 to-transparent border border-[#FF6B00]/30 rounded-2xl p-8 text-center">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-[#FF6B00]" />
                <h2 className="text-white font-bold text-xl">More is on the way</h2>
              </div>
              <p className="text-[#dfddd6] text-sm mb-6 max-w-md mx-auto">
                We're building more tools for registered accounts. Sign in now and you'll get
                them automatically as they ship &mdash; no extra setup later.
              </p>
              <Link
                to="/register"
                className="inline-flex items-center gap-1.5 px-6 py-3 bg-[#FF6B00] hover:bg-[#e05f00] text-white font-semibold rounded-full transition-colors"
              >
                Create Your Free Account <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>

      {/* Drawer backdrop */}
      <div
        className={`fixed inset-0 bg-black/60 z-40 transition-opacity duration-300 ${
          drawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setDrawerOpen(false)}
        aria-hidden="true"
      />

      {/* Tool - slides in from the right. Wide: the tool's own CSS expects up to ~1040px. */}
      <div
        className={`fixed inset-y-0 right-0 z-50 w-[95vw] max-w-4xl bg-[#1a1c1b] border-l border-[#4a3520] shadow-2xl overflow-y-auto transition-transform duration-300 ease-in-out ${
          drawerOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="bg-remover-drawer"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#4a3520] sticky top-0 bg-[#1a1c1b] z-10">
          <h2 className="text-white font-semibold">Background Remover</h2>
          <button onClick={() => setDrawerOpen(false)} className="p-1.5 text-[#888680] hover:text-white" data-testid="close-bg-remover-button">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          <div className="bgr-root" data-testid="background-remover">
            <BackgroundRemover />
          </div>
        </div>
      </div>
    </>
  );
};

export default BackgroundRemoverPage;
